import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ChevronLeft, Truck, ShieldCheck, RefreshCcw, Minus, Plus, Flame, Tag } from "lucide-react";
import { toast } from "sonner";
import { getProduct } from "@/lib/api";
import { formatEUR, DEALS } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import StockBar from "@/components/StockBar";
import CountdownTimer from "@/components/CountdownTimer";
import Stars from "@/components/Stars";
import { getReviews } from "@/lib/reviews";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setSize(null);
    setColor(null);
    setVariant(null);
    setQty(1);
    setActiveImg(0);
    getProduct(slug)
      .then(setProduct)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl text-stone-100 mb-4">Produit introuvable</h1>
        <Link to="/boutique" className="btn-ember rounded-xl px-6 py-3 inline-block">Retour à la boutique</Link>
      </div>
    );
  }
  if (!product) {
    return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Chargement…</div>;
  }

  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const colorImages = product.color_images || {};
  const displayImage = color && colorImages[color] ? colorImages[color] : gallery[activeImg];
  const variants = product.variants || [];
  const activeVariant = variant || (variants.length ? variants[0] : null);
  const activePrice = activeVariant ? activeVariant.price : product.price;
  const discount = Math.round((1 - activePrice / product.original_price) * 100);
  const reviews = getReviews(product.slug);
  const deal = DEALS[product.lookup_key];

  const effectiveProduct = () =>
    activeVariant ? { ...product, lookup_key: activeVariant.lookup_key, price: activeVariant.price } : product;
  const chosenLabel = () => {
    if (activeVariant) return activeVariant.label;
    return [color, size].filter(Boolean).join(" · ") || null;
  };

  const validateOptions = () => {
    if (product.colors && product.colors.length > 0 && !color) {
      toast.error("Veuillez choisir une couleur");
      return false;
    }
    if (product.sizes && product.sizes.length > 0 && !size) {
      toast.error("Veuillez choisir une taille");
      return false;
    }
    return true;
  };

  const handleAdd = () => {
    if (!validateOptions()) return;
    addItem(effectiveProduct(), chosenLabel(), qty);
  };

  const buyNow = () => {
    if (!validateOptions()) return;
    addItem(effectiveProduct(), chosenLabel(), qty);
    navigate("/paiement");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pt-8 pb-28 lg:py-12" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-stone-400 hover:text-amber-400 mb-8 text-sm" data-testid="back-button">
        <ChevronLeft className="h-4 w-4" /> Retour
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <motion.div
            key={displayImage}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl overflow-hidden gothic-card aspect-square bg-[#0b0810]"
          >
            <img src={displayImage} alt={product.name} className="w-full h-full object-cover" />
          </motion.div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-4">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-amber-500" : "border-transparent opacity-60"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="eyebrow mb-2">{product.category}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-50 leading-tight mb-4">
            {product.name}
          </h1>

          <a href="#avis" className="inline-flex items-center gap-2 mb-4 group/rev">
            <Stars value={reviews.rating} size={16} />
            <span className="text-sm font-semibold text-stone-200">{reviews.rating.toFixed(1)}</span>
            <span className="text-sm text-stone-500 underline underline-offset-2 group-hover/rev:text-amber-400">
              ({reviews.count} avis)
            </span>
          </a>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-mono-tech text-4xl font-extrabold text-amber-400">{formatEUR(activePrice)}</span>
            {product.original_price > activePrice && (
              <>
                <span className="text-lg text-stone-500 line-through font-mono-tech">{formatEUR(product.original_price)}</span>
                <span className="px-2 py-1 rounded-md bg-red-600 text-white text-xs font-extrabold">-{discount}%</span>
              </>
            )}
          </div>

          <p className="text-stone-300 leading-relaxed mb-6">{product.description}</p>

          {deal && (
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2.5 rounded-xl bg-green-500/10 border border-green-500/30 text-green-300 text-sm font-bold">
              <Tag className="h-4 w-4" /> Offre spéciale : {deal.label}
            </div>
          )}

          <div className="gothic-card rounded-xl p-4 mb-6">
            <StockBar soldPct={product.sold_pct} stock={product.stock} className="mb-4" />
            <div className="flex items-center gap-2 text-sm text-red-300">
              <Flame className="h-4 w-4" />
              <span>L'offre Halloween se termine dans :</span>
            </div>
            <div className="mt-3"><CountdownTimer /></div>
          </div>

          {variants.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-stone-200 mb-2">Quantité — plus vous en prenez, moins c'est cher</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.lookup_key}
                    onClick={() => setVariant(v)}
                    data-testid={`variant-${v.lookup_key}`}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                      activeVariant?.lookup_key === v.lookup_key
                        ? "bg-amber-500 text-[#08060B] border-amber-500"
                        : "border-amber-900/40 text-stone-300 hover:border-amber-600"
                    }`}
                  >
                    {v.label} · {formatEUR(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors && product.colors.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-stone-200 mb-2">
                Couleur{color ? <span className="text-stone-400 font-normal"> — {color}</span> : ""}
              </p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => {
                  const swatch = { Noir: "#141018", Rouge: "#b91c1c", Violet: "#7c3aed", Vert: "#15803d" }[c] || "#555";
                  return (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      title={c}
                      data-testid={`color-${c}`}
                      className={`h-10 w-10 rounded-full border-2 transition-all ${
                        color === c ? "border-amber-500 ring-2 ring-amber-500/40 scale-110" : "border-white/15 hover:border-amber-600"
                      }`}
                      style={{ backgroundColor: swatch }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {product.sizes && product.sizes.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold text-stone-200 mb-2">Taille</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${
                      size === s ? "bg-amber-500 text-[#08060B] border-amber-500" : "border-amber-900/40 text-stone-300 hover:border-amber-600"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1 gothic-card rounded-xl p-1">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-10 w-10 rounded-lg hover:bg-white/5 flex items-center justify-center text-stone-300" data-testid="qty-decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-mono-tech font-bold">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="h-10 w-10 rounded-lg hover:bg-white/5 flex items-center justify-center text-stone-300" data-testid="qty-increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button onClick={handleAdd} className="btn-ember rounded-xl flex-1 py-3.5 inline-flex items-center justify-center gap-2" data-testid="add-to-cart-detail">
              <ShoppingCart className="h-5 w-5" /> Ajouter au panier
            </button>
          </div>

          <button onClick={buyNow} className="w-full py-3.5 rounded-xl border-2 border-amber-500 text-amber-400 font-bold hover:bg-amber-500/10 transition-colors mb-8" data-testid="buy-now-button">
            Acheter maintenant
          </button>

          <div className="grid grid-cols-3 gap-4 border-t border-amber-900/20 pt-6">
            {[
              { icon: Truck, t: "Livraison 48h" },
              { icon: ShieldCheck, t: "Paiement sécurisé" },
              { icon: RefreshCcw, t: "Retours 14j" },
            ].map((f) => (
              <div key={f.t} className="text-center">
                <f.icon className="h-6 w-6 text-amber-500 mx-auto mb-1.5" />
                <p className="text-xs text-stone-400">{f.t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section id="avis" className="mt-16 border-t border-amber-900/20 pt-12" data-testid="reviews-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="eyebrow mb-2">Avis clients</p>
            <h2 className="font-display text-3xl font-bold text-stone-100">Ce qu'en disent nos clients</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono-tech text-4xl font-extrabold text-amber-400">{reviews.rating.toFixed(1)}</span>
            <div>
              <Stars value={reviews.rating} size={18} />
              <p className="text-xs text-stone-500 mt-1">{reviews.count} avis vérifiés</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {reviews.items.map((r, i) => (
            <div key={i} className="gothic-card rounded-xl p-5" data-testid={`review-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-100">{r.name}</p>
                    {r.verified && <p className="text-[11px] text-green-400 font-semibold">✓ Achat vérifié</p>}
                  </div>
                </div>
                <Stars value={r.stars} size={14} />
              </div>
              <p className="text-sm text-stone-300 leading-relaxed">{r.text}</p>
              <p className="text-xs text-stone-600 mt-3">{r.date}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sticky mobile buy bar */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#08060B]/95 backdrop-blur-md border-t border-amber-900/40 px-4 py-3 flex items-center gap-3"
        data-testid="mobile-buy-bar"
      >
        <div className="leading-none shrink-0">
          <p className="font-mono-tech text-xl font-extrabold text-amber-400">{formatEUR(activePrice)}</p>
          {product.original_price > activePrice && (
            <p className="text-xs text-stone-500 line-through font-mono-tech">{formatEUR(product.original_price)}</p>
          )}
        </div>
        <button
          onClick={handleAdd}
          className="btn-ember rounded-xl flex-1 py-3 inline-flex items-center justify-center gap-2"
          data-testid="mobile-add-to-cart"
        >
          <ShoppingCart className="h-5 w-5" /> Ajouter au panier
        </button>
      </div>
    </div>
  );
}
